import PropTypes from "prop-types";
import React, { createContext } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { useLocation } from "@swa-ui/browser";
import { merge } from "@swa-ui/object";
import { usePersistedState } from "@swa-ui/persistence";
import { globToRegex } from "@swa-ui/string";

import { getPageId } from "../getPageId";

/**
 * AppSettingsProvider provides all default application and bootstrap configuration settings in a React context so the
 * configuration settings is available to components in an app's render tree.
 *
 * appSettings - Default settings configured in SPA's main app component.
 * defaultAppSettings - An app's settings without page specific settings.
 * getAppSettingForPathname - Method to retrieve app's settings including bootstrap data.
 * overrideAppSettings - Method that saves an object of settings that can be used to override the default settings
 */

export const AppSettingsContext = createContext();
export const AppSettingsProvider = (props) => {
  const { appId, children, defaultAppSettings } = props;
  const { pathname } = useLocation();
  const [appSettingsOverrides, setAppSettingsOverrides] = usePersistedState({});

  return (
    <AppSettingsContext.Provider
      value={{
        appSettings: getAppSettingsForCurrentPathname(),
        defaultAppSettings: getDefaultAppSettings(),
        getAppSettingForPathname,
        overrideAppSettings,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );

  function getDefaultAppSettings() {
    const bootstrapAppSettings = getBootstrapData(`app-settings/${appId}-app-settings`);

    return {
      ...defaultAppSettings?.default,
      ...bootstrapAppSettings?.default,
      ...appSettingsOverrides,
    };
  }

  function getAppSettingsForCurrentPathname() {
    return getAppSettingsForPathname(pathname);
  }

  function getAppSettingForPathname(path, key, defaultValue) {
    return getAppSettingsForPathname(path)?.[key] ?? defaultValue;
  }

  function getAppSettingsForPathname(path) {
    const bootstrapAppSettings = getBootstrapData(`app-settings/${appId}-app-settings`);
    const pageId = getPageId(path);
    const patternRules = bootstrapAppSettings?.patterns || [];

    return merge(
      defaultAppSettings?.default,
      defaultAppSettings?.[pageId],
      bootstrapAppSettings?.default,
      getBootstrapPatternMatches(patternRules, pageId),
      bootstrapAppSettings?.[pageId],
      appSettingsOverrides
    );
  }

  function getBootstrapPatternMatches(patterns, id) {
    /** Example bootstrap app-settings structure
    {
      "default": {
        // global settings
      },
      "patterns": [
        {
          "globPattern": "vacations-*",
          "settings": {
            "mboxes": ["techTargetTest14", "techTargetTest15"],
            "placementsBlocked": false
          }
        },
        {
          "globPattern": "special-offers-*",
          "settings": {
            "mboxes": ["specialOffersGrid1"],
            "enableFeatureX": true
          }
        }
      ],
      "vacations-index": {
        // specific page settings
      }
    }
     */

    return patterns.reduce((accumulator, pattern) => {
      const shouldMergeSettings = pattern.globPattern && globToRegex(pattern.globPattern).test(id);

      return shouldMergeSettings ? merge(accumulator, pattern.settings) : accumulator;
    }, {});
  }

  function overrideAppSettings(newAppSettings) {
    setAppSettingsOverrides(newAppSettings);
  }
};

AppSettingsContext.displayName = "AppSettingsContext";

AppSettingsProvider.displayName = "AppSettingsProvider";
AppSettingsProvider.propTypes = {
  /**
   * String that identifies the app for when making requests. The value, something like: 'gift-card', is added to
   * request headers.
   */
  appId: PropTypes.string.isRequired,

  /** Current page's content to be rendered inside this AppSettingsProvider wrapper. */
  children: PropTypes.node.isRequired,

  /** Application defined configuration settings. */
  defaultAppSettings: PropTypes.object,
};
